import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';

const DEFAULT_MIN_CHARS = 2;
const DEFAULT_DEBOUNCE_MS = 300;
const EMPTY_REQUIRED_GROUPS = [];
const STRUCTURED_DEBOUNCE_MS = 500;
const DATE_SEARCH_FORMATS = [
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MM-DD-YYYY',
  'M-D-YYYY',
  'MMDDYYYY',
];

const normalizeDateSearchValue = (value) => {
  if (!value) return null;

  if (moment.isMoment(value)) {
    return value.isValid() ? value.format('YYYY-MM-DD') : null;
  }

  if (value instanceof Date) {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const parsed = moment(trimmed, DATE_SEARCH_FORMATS, true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const buildErrorMessage = (error) => {
  if (!error) return null;

  if (error.response?.data?.error) {
    return error.response.data.error;
  }

  return 'Unable to search patients right now.';
};

const useEmrPatientSearch = ({
  credentialId,
  enabled,
  searchFields,
  minQueryLength = DEFAULT_MIN_CHARS,
  debounceMs,
  limit = 10,
  requiredGroups = EMPTY_REQUIRED_GROUPS,
  location,
}) => {
  const [fieldValues, setFieldValues] = useState({});
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const cancelRef = useRef({ cancelled: false });

  // Determine mode
  const isStructuredSearch = searchFields?.length > 0
    && !searchFields.every(f => f.key === 'query');

  // Use longer debounce for structured mode to allow tabbing between fields
  const effectiveDebounce = debounceMs
    ?? (isStructuredSearch ? STRUCTURED_DEBOUNCE_MS : DEFAULT_DEBOUNCE_MS);

  // Single-field updater (stable reference via useCallback)
  const setFieldValue = useCallback((key, value) => {
    setFieldValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => {
    setFieldValues({});
    setResults([]);
    setIsLoading(false);
    setError(null);
  }, []);

  // Reset on credential/enabled change
  useEffect(() => {
    reset();
    cancelRef.current.cancelled = true;
  }, [credentialId, enabled, location]);

  // Compute whether we have enough input to trigger search.
  // Date fields are presence-checked (not length-checked).
  const canSearch = useMemo(() => {
    if (!enabled || !credentialId) return false;

    if (!searchFields?.length) {
      // Legacy mode: check fieldValues.query length
      return (fieldValues.query || '').trim().length >= minQueryLength;
    }

    // Name fields are validated as a combined string to match the backend's
    // resolve_query_from_params, which joins them before length-checking.
    const nameKeys = ['first_name', 'last_name'];

    // Check that all required fields are satisfied
    const requiredFields = searchFields.filter(f => f.required);
    const hasAllRequired = requiredFields.every(f => {
      const val = fieldValues[f.key];
      if (f.component === 'date') {
        return normalizeDateSearchValue(val) != null;
      }
      // For name fields, require presence but defer length to the combined check
      if (nameKeys.includes(f.key)) return (val || '').trim().length > 0;
      return (val || '').trim().length >= minQueryLength;
    });

    if (!hasAllRequired) return false;

    const satisfiesField = (field) => {
      const val = fieldValues[field.key];
      if (field.component === 'date') {
        return normalizeDateSearchValue(val) != null;
      }
      if (nameKeys.includes(field.key)) return (val || '').trim().length > 0;
      return (val || '').trim().length >= minQueryLength;
    };

    if (requiredGroups.length > 0) {
      const fieldsByKey = searchFields.reduce((memo, field) => ({
        ...memo,
        [field.key]: field,
      }), {});

      return requiredGroups.some(group => group.every((key) => {
        const field = fieldsByKey[key];
        return field && satisfiesField(field);
      }));
    }

    // For name fields that the backend joins, check the *combined* length
    // so canSearch and resolve_query_from_params agree on what's submittable.
    const combinedName = nameKeys
      .map(k => (fieldValues[k] || '').trim())
      .filter(Boolean)
      .join(' ');
    const hasValidName = combinedName.length >= minQueryLength;

    // At least one field must have meaningful input
    const hasAnyInput = searchFields.some(f => {
      if (nameKeys.includes(f.key)) return hasValidName;
      const val = fieldValues[f.key];
      if (f.component === 'date') {
        return normalizeDateSearchValue(val) != null;
      }
      return (val || '').trim().length >= minQueryLength;
    });

    return hasAnyInput;
  }, [fieldValues, searchFields, minQueryLength, enabled, credentialId, requiredGroups]);

  // Stable serialization of fieldValues for useEffect dependency.
  const fieldValuesKey = useMemo(
    () => JSON.stringify(fieldValues),
    [fieldValues]
  );

  // Debounced search effect
  useEffect(() => {
    if (!canSearch) {
      setResults([]);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    setError(null);

    // Create cancellation token before the timer so stale requests
    // that resolve between effect runs are always discarded (#2).
    const cancellationToken = { cancelled: false };
    cancelRef.current = cancellationToken;

    const timerId = setTimeout(() => {
      if (cancellationToken.cancelled) return;

      setIsLoading(true);

      // Build request params based on mode
      let requestParams;
      if (isStructuredSearch) {
        const searchParams = {};
        searchFields.forEach(f => {
          const val = fieldValues[f.key];
          if (f.component === 'date') {
            const normalizedDate = normalizeDateSearchValue(val);
            if (normalizedDate) searchParams[f.key] = normalizedDate;
          } else {
            const trimmed = (val || '').trim();
            if (trimmed) searchParams[f.key] = trimmed;
          }
        });
        requestParams = {
          emr_integration_credential_id: credentialId,
          search_params: searchParams,
          limit,
        };
      } else {
        const queryVal = (fieldValues.query || '').trim();
        requestParams = {
          emr_integration_credential_id: credentialId,
          query: queryVal,
          limit,
        };
      }

      if (location) {
        requestParams.location = location;
      }

      axios.get('/api/v1/emr_patients', { params: requestParams })
        .then((response) => {
          if (cancellationToken.cancelled) return;
          setResults(response.data?.patients || []);
          setIsLoading(false);
        })
        .catch((err) => {
          if (cancellationToken.cancelled) return;
          setError(buildErrorMessage(err));
          setIsLoading(false);
        });
    }, effectiveDebounce);

    return () => {
      clearTimeout(timerId);
      cancellationToken.cancelled = true;
    };
  }, [fieldValuesKey, canSearch, credentialId, isStructuredSearch, searchFields, limit, effectiveDebounce, location]);

  return useMemo(() => ({
    fieldValues,
    setFieldValue,
    results,
    isLoading,
    error,
    canSearch,
    reset,
  }), [fieldValues, setFieldValue, results, isLoading, error, canSearch, reset]);
};

export default useEmrPatientSearch;
